import { CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { inject } from '@angular/core';
import { Observable, filter, map, take } from 'rxjs';
import { ProjectService } from './services/project.service';
import { initialDeepLinkProject } from './services/project-deep-link';
import {
  decideProjectAccess,
  isWorthReturningTo,
  SELECTION_ROUTE,
} from './services/project-selection-gate';
import { rememberIntendedUrl } from './auth/return-to';

/** Where a restored selection is persisted. Owned by main-layout; read here to predict it. */
export const LAST_SELECTED_PROJECT_KEY = 'lastSelectedProject';

/**
 * Is a project going to be selected, even though it is not yet?
 *
 * A cold load straight to a project tab — a bookmark, a shared link, a reload —
 * arrives with nothing selected and a restore still to come, because the restore
 * lives in MainLayoutComponent and that component is not created until the route
 * ACTIVATES, which is after this guard runs. Answering "no project" there would
 * bounce every reload to the picker for a project the user already had open.
 *
 * So a pending INTENT counts as a selection. Both sources are read through their
 * existing owners rather than reimplemented: initialDeepLinkProject() captures the
 * URL at module evaluation (before Angular bootstraps, which is the only moment it
 * is reliably there), and the persisted key is the same one main-layout writes.
 */
export function hasPendingSelection(storage: Pick<Storage, 'getItem'> | null): boolean {
  if (initialDeepLinkProject()) return true;
  try {
    return !!storage?.getItem(LAST_SELECTED_PROJECT_KEY);
  } catch {
    // Private browsing and blocked site data both throw on access. Treat as "no
    // intent" rather than failing the navigation.
    return false;
  }
}

/**
 * Project-dependent routes require a project.
 *
 * WHY A GUARD AND NOT JUST A DISABLED TAB. The greyed tab is the affordance — it
 * tells the user what to do. This is the enforcement, and the two are not
 * interchangeable: a disabled `<a>` still navigates if a routerLink binding is
 * restored, because Angular's RouterLink handles the click itself and
 * preventDefault on the DOM event does not stop it. A mutation that re-enabled the
 * binding passed the click-refusal tests and would have shipped. Nothing reaches a
 * project screen without a project now, however it was reached — a typed URL, a
 * stale bookmark, or a template edit.
 *
 * WAIT, NEVER REDIRECT-WHILE-RESOLVING. Same discipline as userAuthGuard, and for
 * the same reason: it answers only once the project list has SETTLED (loaded or
 * failed). Deciding early is what turned a guard into a 99-navigation storm.
 */
export const projectSelectedGuard: CanActivateFn = (
  _route,
  state: RouterStateSnapshot
): Observable<boolean> => {
  const projectService = inject(ProjectService);
  const router = inject(Router);

  return projectService.projectListLoad$.pipe(
    // "Settled" is loaded OR failed. A failed load must not hang the app on a
    // spinner: the user still gets the selection screen, which shows the failure
    // and offers a retry.
    filter((load) => load.kind === 'loaded' || load.kind === 'failed'),
    take(1),
    map(() => {
      const selected =
        !!projectService.selectedProjectName ||
        hasPendingSelection(typeof localStorage === 'undefined' ? null : localStorage);

      const decision = decideProjectAccess({
        projectSelected: selected,
        selectionResolved: true,   // the filter above is the wait
        intendedUrl: state.url,
      });

      if (decision.kind === 'allow') return true;

      // Remembered through web.ui's shared in-app return-to (cec5c9a), NOT a query
      // parameter: theirs keeps the destination out of logs, pasted links and
      // referrers, and is the single implementation this guard was built to call.
      if (decision.kind === 'redirect' && isWorthReturningTo(decision.intendedUrl)) {
        rememberIntendedUrl(decision.intendedUrl);
      }
      router.navigate([SELECTION_ROUTE]);
      return false;
    })
  );
};
